import React, { useEffect, useState } from 'react';
import {
  DownloadRounded as DownloadRoundedIcon,
  Delete as DeleteIcon,
  FlagRounded as FlagRoundedIcon,
  MoreVertRounded as MoreVertRoundedIcon,
  ArrowUpwardRounded as ArrowUpwardRoundedIcon,
  ArrowDownwardRounded as ArrowDownwardRoundedIcon,
  SlowMotionVideoRounded as SlowMotionVideoRoundedIcon,
  ControlPointDuplicateRounded as ControlPointDuplicateRoundedIcon,
} from '@mui/icons-material';
import { Fade, IconButton, Paper, Popper } from '@mui/material';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Tooltip from '@src/components/Tooltip';
import { StyledMenuItem } from './index.style';

const MoreOptionBlock = ({
  onOpenDeleteDialog,
  onOpenReportDialog,
  onOpenMoreOptionBlock,
  openMoreOptionBlock,
  onDownloadBlockAudio,
  isDisabledDownloadBlockAudio,
  canDownloadBlockAudio,
  isDisabledDeleteBlock,
  isDubbingProject,
  onMoveBlockUp,
  onMoveBlockDown,
  onDuplicateBlock,
  onReSynthesizeBlock,
  isFirstBlock,
  isLastBlock,
  showReSynthesize,
  isDisabledMoveBlock,
  isDisabledReSynthesize,
  reSynthesizeTooltip,
  isRewriting,
  hideDeleteBlock = false,
}) => {
  const { t } = useTranslation();
  const isGeneratingNarration = useSelector(
    (state) => state.project.isGeneratingNarration,
  );
  const [anchorBlockOption, setAnchorBlockOption] = useState(null);
  const disableReSynthesize = isDisabledReSynthesize || isGeneratingNarration;
  // `disabled` alone does NOT stop this item firing. MenuItem is a ButtonBase
  // rendered as an <li>, which takes no `disabled` attribute — MUI blocks the
  // click with `pointer-events: none` on .Mui-disabled, and StyledMenuItem
  // overrides that back to `auto` so the not-allowed cursor stays visible. The
  // handler has to check for itself (as reSynthesize below already does).
  const disableDeleteBlock = isDisabledDeleteBlock || isRewriting;

  const refClickOutSide = useClickOutside(() => onOpenMoreOptionBlock(false));

  // The menu is portalled to <body>, so the list it belongs to neither clips it
  // nor carries it: a scroll leaves it pinned to a row on its way past the
  // list's bottom edge, over whatever sits below. Rows are virtualized as well,
  // so far enough down the anchor unmounts and the menu is left pointing at a
  // detached node, with nothing left to reposition against. Dismissing covers
  // both. `scroll` does not bubble — only the capture phase sees a scroll that
  // happened on an ancestor.
  useEffect(() => {
    if (!openMoreOptionBlock) return undefined;

    const close = () => onOpenMoreOptionBlock(false);
    window.addEventListener('scroll', close, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
  }, [openMoreOptionBlock, onOpenMoreOptionBlock]);

  const handleOpenDeleteDialog = () => {
    if (disableDeleteBlock) return;
    onOpenDeleteDialog();
  };

  return (
    <div ref={refClickOutSide}>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setAnchorBlockOption(e.currentTarget);
          onOpenMoreOptionBlock(!openMoreOptionBlock);
        }}
        className="editor-toolbar-btn"
      >
        <MoreVertRoundedIcon
          style={{
            color: COLOR.darkBlue,
            opacity: 0.64,
          }}
        />
      </IconButton>
      <Popper
        transition
        style={{
          zIndex: 1000,
          width: '180px',
          color: '#1E164E',
          boxShadow: '0px 0px 20px 2px rgba(0, 0, 0, 0.1)',
        }}
        anchorEl={anchorBlockOption}
        open={openMoreOptionBlock && Boolean(anchorBlockOption)}
        placement="bottom-end"
      >
        {({ TransitionProps }) => (
          <Fade
            style={{ padding: '4px 0px', boxShadow: 'none' }}
            {...TransitionProps}
            timeout={350}
          >
            <Paper onMouseDown={(e) => e.stopPropagation()}>
              {onMoveBlockUp && (
                <StyledMenuItem
                  disabled={isFirstBlock || isDisabledMoveBlock}
                  key="moveBlockUp"
                  onClick={() => {
                    onMoveBlockUp();
                    onOpenMoreOptionBlock(false);
                  }}
                >
                  <span>
                    <IconButton className="option-btn" isDisableHover>
                      <ArrowUpwardRoundedIcon
                        style={{ color: COLOR.darkBlue, fontSize: '24px' }}
                      />
                    </IconButton>
                  </span>
                  <span>{t('moveUp')}</span>
                </StyledMenuItem>
              )}

              {onMoveBlockDown && (
                <StyledMenuItem
                  disabled={isLastBlock || isDisabledMoveBlock}
                  key="moveBlockDown"
                  onClick={() => {
                    onMoveBlockDown();
                    onOpenMoreOptionBlock(false);
                  }}
                >
                  <span>
                    <IconButton className="option-btn" isDisableHover>
                      <ArrowDownwardRoundedIcon
                        style={{ color: COLOR.darkBlue, fontSize: '24px' }}
                      />
                    </IconButton>
                  </span>
                  <span>{t('moveDown')}</span>
                </StyledMenuItem>
              )}

              {showReSynthesize && onReSynthesizeBlock && (
                <Tooltip
                  arrow
                  title={reSynthesizeTooltip || t('convertToAudioToListen')}
                  placement="left"
                >
                  <StyledMenuItem
                    key="reSynthesize"
                    disabled={disableReSynthesize}
                    onClick={() => {
                      if (disableReSynthesize) return;
                      onReSynthesizeBlock();
                      onOpenMoreOptionBlock(false);
                    }}
                  >
                    <span>
                      <IconButton className="option-btn" isDisableHover>
                        <SlowMotionVideoRoundedIcon
                          style={{ color: COLOR.darkBlue, fontSize: '24px' }}
                        />
                      </IconButton>
                    </span>
                    <span>{t('reSynthesize')}</span>
                  </StyledMenuItem>
                </Tooltip>
              )}

              {onDuplicateBlock && (
                <StyledMenuItem
                  key="duplicateBlock"
                  onClick={() => {
                    onDuplicateBlock();
                    onOpenMoreOptionBlock(false);
                  }}
                >
                  <span>
                    <IconButton className="option-btn" isDisableHover>
                      <ControlPointDuplicateRoundedIcon
                        style={{ color: COLOR.darkBlue, fontSize: '24px' }}
                      />
                    </IconButton>
                  </span>
                  <span>{t('duplicateBlock')}</span>
                </StyledMenuItem>
              )}

              {(!isDubbingProject || canDownloadBlockAudio) && (
                <StyledMenuItem
                  disabled={isDisabledDownloadBlockAudio}
                  key="downloadBlockAudio"
                  onClick={onDownloadBlockAudio}
                >
                  <span>
                    <IconButton className="option-btn" isDisableHover>
                      <DownloadRoundedIcon
                        style={{
                          color: COLOR.darkBlue,
                          fontSize: '24px',
                        }}
                      />
                    </IconButton>
                  </span>
                  <span>{t('download')}</span>
                </StyledMenuItem>
              )}

              {!isDubbingProject && (
                <StyledMenuItem
                  key="reportBlockAudio"
                  onClick={onOpenReportDialog}
                >
                  <span>
                    <IconButton className="option-btn" isDisableHover>
                      <FlagRoundedIcon
                        style={{
                          color: COLOR.darkBlue,
                          fontSize: '24px',
                        }}
                      />
                    </IconButton>
                  </span>
                  <span>{t('errorReport')}</span>
                </StyledMenuItem>
              )}

              {!hideDeleteBlock && (
                <StyledMenuItem
                  disabled={disableDeleteBlock}
                  key="handleOpenDeleteBlock"
                  onClick={handleOpenDeleteDialog}
                >
                  <IconButton className="option-btn" isDisableHover>
                    <DeleteIcon
                      style={{
                        color: '#FF3B30',
                        fontSize: '24px',
                      }}
                    />
                  </IconButton>
                  <span>{t('delete')}</span>
                </StyledMenuItem>
              )}
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
};

export default MoreOptionBlock;
